import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Logo from '../components/common/logo';
import Input from '../components/common/input';
import Button from '../components/common/button';
import Checkbox from '../components/common/checkbox';
import { signUpSchema, validateWithZod } from '../validators/authValidator';
import { signUpUser } from '../store/actions/authActions';
import { validateFullName } from '../utils/validators';
import illustrationImage from '../assets/images/Illustration.png';

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'fullName') {
      const fullNameError = validateFullName(value);
      setErrors(prev => {
        if (fullNameError) {
          return { ...prev, fullName: fullNameError };
        }
        const { fullName, ...rest } = prev;
        return rest;
      });
    } else if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const validation = validateWithZod(signUpSchema, formData);

    if (!validation.success) {
      setErrors(validation.errors);
      return null;
    }

    setErrors({});
    return validation.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validatedData = validateForm();
    if (!validatedData) {
      return;
    }

    try {
      await dispatch(signUpUser({
        fullName: validatedData.fullName,
        email: validatedData.email,
        password: validatedData.password,
        confirmPassword: validatedData.confirmPassword,
        agreeToTerms: validatedData.agreeToTerms
      }, navigate));
    } catch (error) {
      console.error('Sign up error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:p-0 lg:items-stretch">
      <div className="w-full lg:max-w-none max-w-md lg:flex lg:flex-1">
        <div className="bg-white rounded-2xl lg:rounded-none shadow-2xl lg:shadow-none overflow-hidden flex-1 lg:flex">
          <div className="flex-1 lg:flex w-full">
            <div className="p-6 sm:p-8 lg:p-8 xl:p-10 flex flex-col justify-center lg:w-1/2">
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <Logo size="default" />
                  <h1 className="text-2xl sm:text-3xl font-bold text-[#8B4513]">Pangolin Script</h1>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">AI-powered script generation for YouTube content creators</p>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 leading-tight">Create Your Account</h2>
                <p className="text-gray-600 text-sm leading-relaxed">Sign up to start your scriptwriting journey</p>
              </div>
              <div className="fade-in">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          label="Full Name"
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="John Doe"
          error={errors.fullName}
          required
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          }
        />
        <Input
          label="Email address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="abc@gmail.com"
          error={errors.email}
          required
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          }
        />
        <Input
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="••••••••••••"
          error={errors.password}
          required
        />
        <Input
          label="Confirm password"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="••••••••••••"
          error={errors.confirmPassword}
          required
        />
        <Checkbox
          label={
            <span className="text-sm">
              By signing up, I agree to the{' '}
              <a href="/terms" className="text-primary hover:text-primary-dark font-medium hover:underline">
                Terms & Conditions
              </a>
              {' '}and{' '}
              <a href="/privacy" className="text-primary hover:text-primary-dark font-medium hover:underline">
                Privacy Policy
              </a>
            </span>
          }
          name="agreeToTerms"
          checked={formData.agreeToTerms}
          onChange={handleChange}
          error={errors.agreeToTerms}
        />
        <Button
          type="submit"
          fullWidth
          loading={loading}
          disabled={loading}
        >
          Create Account
        </Button>

        <p className="text-center text-sm text-gray-600 pt-0">
          Already have an account?{' '}
          <Link
            to="/signin"
            className="text-primary hover:text-primary-dark font-semibold transition-colors hover:underline"
          >
            Sign in
          </Link>
        </p>
      </form>
              </div>
              <div className="mt-4 lg:hidden text-center">
                <p className="text-xs text-gray-500">© {new Date().getFullYear()} PangolinScript. All rights reserved.</p>
              </div>
            </div>
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-amber-700 via-amber-800 to-yellow-800 items-center justify-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-600/20 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-600/20 rounded-full -ml-24 -mb-24"></div>
              <img src={illustrationImage} alt="Scriptwriting Illustration" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;